import { Icon } from '../icon/Icon'
import { memo } from 'react'
import type { MessageData } from '@/stores/chatStore'
import { useAuthStore } from '@/stores/authStore'
import { useAgentsStore } from '@/stores/agentsStore'
import { useProvidersStore } from '@/stores/providersStore'
import { ProviderIcon } from '@/components/admin/ProviderIcon'
import { QualityBadge } from './QualityBadge'
import { resolveColor } from '@/lib/utils'
import { useI18n } from '@/lib/i18n'

interface MessageHeaderProps {
  message: MessageData
  /** true если сессия — дочерняя (сабагент) */
  isSubagent?: boolean
  /** Клик по origin-бейджу — открыть родительскую сессию */
  onOriginClick?: () => void
  /** true — вместо времени показать индикатор обработки (три прыгающие точки) */
  processing?: boolean
  /**
   * ADR-046 §7.1: рендерить только мета-часть (модель/качество/время) — для
   * шапки `Notice` (сводка/отладка), без бейджей автора/агента/Summary.
   */
  metaOnly?: boolean
  /**
   * Ответ агента развёрнут на всю ширину области чата. Кнопка разворота
   * рендерится **только** при заданном `onToggleExpand` — то есть у ответа
   * агента, а не у user-реплик и служебных блоков.
   */
  expanded?: boolean
  /** Тумблер разворота ответа (стабильная ссылка из `ChatMessage`). */
  onToggleExpand?: () => void
}

/**
 * Подпись автора реплики.
 *
 * `message.author` для своих сообщений — username (в standalone — `local`/`user`,
 * в старых сообщениях `attach_workspace_file` бывало `User`), для внешних — имя
 * отправителя (`payload.from`, ADR-089). Своё сообщение показываем именем текущего
 * пользователя из профиля; чужое — настоящим автором, иначе в шапке всегда стояло
 * имя зрителя (чужой отправитель выглядел как он сам).
 *
 * Внешнее событие без имени отправителя (`metadata.event_id`, но `author` не
 * проставлен) получает нейтральную подпись `externalFallback`: подставлять имя
 * зрителя для чужого сообщения нельзя.
 */
function resolveAuthor(message: MessageData, externalFallback: string): string {
  const { user, profile } = useAuthStore.getState()
  const own = profile?.display_name || user?.username || ''
  const author = typeof message.author === 'string' ? message.author.trim() : ''
  const isExternalEvent = message.metadata?.event_id !== undefined
  if (!author) return isExternalEvent ? externalFallback : own
  const normalized = author.toLowerCase()
  const username = user?.username
  if (
    (typeof username === 'string' && username.length > 0
      && normalized === username.toLowerCase())
    || normalized === 'local'
    || normalized === 'user'
  ) {
    return own
  }
  return author
}

function MessageHeaderInner({ message, isSubagent, onOriginClick, processing, metaOnly, expanded, onToggleExpand }: MessageHeaderProps) {
  const { t } = useI18n()
  const time = message.timestamp || message.created_at
  const formatted = time
    ? new Date(time).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    : ''

  const isUser = message.role === 'user'
  const isSummary = message.role === 'summary'

  const agents = useAgentsStore((s) => s.agents)
  const providers = useProvidersStore((s) => s.providers)

  const agentInfo = message.agent_role
    ? agents.find((a) => a.id === message.agent_role)
    : undefined
  const agentColor = resolveColor(agentInfo?.color ?? null)

  const providerIcon = message.provider
    ? providers.find((p) => p.name === message.provider)?.icon || ''
    : ''
  const providerUrl = message.provider
    ? providers.find((p) => p.name === message.provider)?.url
    : undefined

  const isInherited = message.origin === 'inherited'

  // В дочерних сессиях (сабагент):
  //   inherited user     → userBadge  (сообщение написал пользователь в родительской сессии)
  //   inherited assistant → agentBadge (ответ написал агент в родительской сессии)
  //   local сообщения    → agentBadge (промпт/ответ сабагента — от агента)
  // В основных сессиях:
  //   user      → userBadge
  //   assistant → agentBadge
  //   summary   → summary badge
  const showUserBadge = isSubagent ? (isInherited && isUser) : isUser
  const showAgentBadge = !isSummary && !!message.agent_role && (
    isSubagent
      ? (isInherited ? !isUser : true)     // сабагент: inherited-ассистент или любое local
      : !isUser                              // основная сессия: не-user → agentBadge
  )
  const agentBadge = showAgentBadge && (
    agentInfo ? (
      <span
        className={`inline-flex items-center gap-1 rounded-sm py-0.5 ${agentColor ? '' : 'text-primary'}`}
        style={agentColor ? { color: agentColor } : undefined}
      >
        <Icon name={agentInfo.icon || 'user-smile'} className="size-3" />
        <span>{agentInfo.display_name || message.agent_role}</span>
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 rounded-sm py-0.5 text-primary">
        <Icon name="user-smile" className="size-3" />
        <span>{message.agent_role}</span>
      </span>
    )
  )

  const userBadge = showUserBadge && (
    <span className="inline-flex items-center gap-1 rounded-sm py-0.5 text-primary">
      <Icon name="user-3" className="size-3" />
      <span>{resolveAuthor(message, t('chat.author.external'))}</span>
    </span>
  )

  const modelBadge = !isUser && message.model && (
    <span className="inline-flex items-center gap-0.5 rounded-sm px-1.5 py-0.5">
      <ProviderIcon icon={providerIcon} baseUrl={providerUrl} size={12} className="shrink-0" />
      <span>{message.model}</span>
    </span>
  )

  const qaBadge = message.qa_pass !== undefined && (
    <QualityBadge qa_pass={message.qa_pass} qa_reason={message.qa_reason} />
  )

  // ADR-046 §7.1: мета шапки Notice (сводка/отладка) — только модель/качество/время.
  if (metaOnly) {
    return (
      <div className="flex items-center gap-1.5 text-[0.6875rem] text-muted-foreground/70">
        {modelBadge}
        {qaBadge}
        {formatted && <span className="tabular-nums">{formatted}</span>}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1.5 text-[0.6875rem] text-muted-foreground/70 -mt-1 mb-2">
      {userBadge}
      {agentBadge}

      {isSummary && (
        <span className="inline-flex items-center gap-1 rounded-sm py-0.5 text-primary">
          <Icon name="subtract" className="size-3" />
          <span>Summary</span>
        </span>
      )}

      {modelBadge}

      {qaBadge}

      {formatted || processing || onToggleExpand || message.origin === 'merged' || message.origin === 'crosspost' ? (
        <span className="ml-auto inline-flex items-center gap-1">
          {onToggleExpand && (
            // Кнопка-иконка разворота ответа. Визуально компактная (14px иконка
            // + p-1); на coarse-pointer зону клика расширяет псевдоэлемент
            // `.message-expand-btn::after` (`styles/index.css`, ADR-044).
            <button
              type="button"
              data-testid="message-expand"
              onClick={onToggleExpand}
              title={expanded ? t('chat.message.collapse') : t('chat.message.expand')}
              aria-label={expanded ? t('chat.message.collapse') : t('chat.message.expand')}
              aria-pressed={expanded === true}
              className="message-expand-btn relative inline-flex cursor-pointer items-center rounded p-1 text-muted-foreground transition-colors hover:bg-interactive-hover hover:text-foreground"
            >
              <Icon name={expanded ? 'fullscreen-exit' : 'fullscreen'} className="size-3.5" />
            </button>
          )}
          {(message.origin === 'merged' || message.origin === 'crosspost') && (
            <button
              title={message.source_msg_id ? `Из сообщения ${message.source_msg_id.slice(0, 8)}...` : undefined}
              onClick={onOriginClick}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[0.625rem] font-medium cursor-pointer hover:opacity-80 transition-opacity border ${
                message.origin === 'merged' ? 'bg-status-info/10 text-status-info border-status-info/20' :
                'bg-status-success/10 text-status-success border-status-success/20'
              }`}
            >
              <Icon
                name={message.origin === 'merged' ? 'links-line' : 'link'}
                className="h-2.5 w-2.5"
              />
              {message.origin === 'merged' ? 'Скопировано' : 'Связано'}
            </button>
          )}
          {processing ? (
            <span className="ml-auto inline-flex gap-0.5" data-testid="processing-indicator">
              <span className="size-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="size-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="size-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: '300ms' }} />
            </span>
          ) : formatted ? (
            <span className="tabular-nums">{formatted}</span>
          ) : null}
        </span>
      ) : null}
    </div>
  )
}

export const MessageHeader = memo(MessageHeaderInner);
